import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import NoDataMessage from "../cards/NoDataMessage";
import {
    BiLogoFacebookCircle,
    BiLogoInstagramAlt
} from "react-icons/bi";
import { RiWhatsappFill } from "react-icons/ri";
import { HiChatBubbleOvalLeftEllipsis } from "react-icons/hi2";
import { PiChatTeardropDotsFill } from "react-icons/pi";

const CHANNEL_CONFIG = {
    whatsapp: {
        label: "WhatsApp",
        icon: RiWhatsappFill,
        bg: "#25D366",
        text: "#FFFFFF"
    },
    facebook: {
        label: "Facebook",
        icon: BiLogoFacebookCircle,
        bg: "#1877F2",
        text: "#FFFFFF"
    },
    instagram: {
        label: "Instagram",
        icon: BiLogoInstagramAlt,
        bg: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
        text: "#FFFFFF"
    },
    webchat: {
        label: "Webchat",
        icon: HiChatBubbleOvalLeftEllipsis,
        bg: "#6366F1",
        text: "#FFFFFF"
    },
};

const TableChatsEvaluation = ({ data = [], metricLabels = {}, metricType = null }) => {

    const [chatsData, setChatsData] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        setChatsData([...data]);
    }, [data]);

    const formatMetricLabel = (metricKey) => {
        if (metricLabels?.[metricKey]) {
            return metricLabels[metricKey];
        }

        if (metricKey === "overall_score") {
            return "Puntaje general";
        }

        return metricKey
            .replaceAll("_", " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getMetricPillStyles = (value) => {
        if (value > 7) {
            return {
                color: "#2f9e44",
                backgroundColor: "rgba(47, 158, 68, 0.18)",
                border: "1px solid #2f9e44",
            };
        }

        if (value >= 5) {
            return {
                color: "#b08900",
                backgroundColor: "rgba(176, 137, 0, 0.18)",
                border: "1px solid #b08900",
            };
        }

        return {
            color: "#e03131",
            backgroundColor: "rgba(224, 49, 49, 0.18)",
            border: "1px solid #e03131",
        };
    };

    const getRowMetrics = (row) => {
        const score = row?.score;
        if (!score || typeof score !== "object") {
            return [];
        }

        const entries = [];

        // Recopilar métricas del chatbot
        if (metricType === null || metricType === "chatbot") {
            if (typeof score.chatbot === "object" && score.chatbot !== null) {
                Object.entries(score.chatbot).forEach(([key, value]) => {
                    if (typeof value === "number" && Number.isFinite(value)) {
                        entries.push([key, value]);
                    }
                });
            }
        }

        // Recopilar métricas del operador
        if (metricType === null || metricType === "operator") {
            if (typeof score.operator === "object" && score.operator !== null) {
                Object.entries(score.operator).forEach(([key, value]) => {
                    if (typeof value === "number" && Number.isFinite(value)) {
                        entries.push([key, value]);
                    }
                });
            }
        }

        return entries.sort(([leftKey], [rightKey]) => {
            if (leftKey === "overall_score") return -1;
            if (rightKey === "overall_score") return 1;
            return leftKey.localeCompare(rightKey, "es", { sensitivity: "base" });
        });
    };

    const renderMetricPill = (metricValue) => {
        return (
            <span
                style={{
                    ...getMetricPillStyles(metricValue),
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 700,
                    minWidth: "52px",
                    textAlign: "center",
                    padding: "2px 8px",
                    whiteSpace: "nowrap",
                }}
            >
                {Number(metricValue.toFixed(2))}
            </span>
        );
    };

    const rowExpansionTemplate = (row) => {
        const metrics = getRowMetrics(row);

        if (!metrics.length) {
            return (
                <div className="px-3 py-2">
                    <NoDataMessage text="Sin parámetros evaluados para esta conversación." />
                </div>
            );
        }

        return (
            <div className="px-3 py-2">
                <div className="row gx-2">
                    {metrics.map(([metricKey, metricValue]) => (
                        <div key={metricKey} className="col-12 col-md-6 col-lg-4 mb-2">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "8px",
                                    border: "1px solid var(--separator-color)",
                                    borderRadius: "10px",
                                    padding: "8px 10px",
                                    backgroundColor: "var(--card-color)",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "12px",
                                        color: "var(--text-color)",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {formatMetricLabel(metricKey)}
                                </span>
                                {renderMetricPill(metricValue)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="card" aria-label="table">
                <DataTable
                    value={chatsData}
                    dataKey="sender_id"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    filterDisplay="row"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    filter
                    responsiveLayout="scroll"
                    aria-label="some"
                >
                    <Column expander style={{ width: "4%" }} />
                    <Column
                        field={"user_name"}
                        header={"Nombre"}
                        style={{ width: "16%" }}
                        filter
                        filterMatchMode={["contains"]}
                        showFilterMenu={false}
                        sortable
                        body={(row) => {
                            return row.user_name ? row.user_name : "Usuario";
                        }}
                    />
                    <Column
                        field={"sender_id"}
                        header={"ID de Conversación"}
                        style={{ width: "22%" }}
                        filter
                        filterMatchMode={["contains"]}
                        showFilterMenu={false}
                        sortable
                        body={(row) => {
                            return row.sender_id ? row.sender_id : "-";
                        }}
                    />
                    <Column
                        field={"channel"}
                        header={"Canal"}
                        style={{ width: "12%" }}
                        filter
                        filterMatchMode="contains"
                        showFilterMenu={false}
                        sortable
                        body={(row) => {
                            const channelKey = row?.channel?.toLowerCase();
                            const config = CHANNEL_CONFIG[channelKey];

                            if (!config) return "-";

                            const { icon: ChannelIcon, bg, text, label } = config;

                            return (
                                <span
                                    style={{
                                        background: bg,
                                        color: text,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "4px 12px",
                                        borderRadius: "9999px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                                    }}
                                >
                                    <ChannelIcon size={16} />
                                    {label}
                                </span>
                            );
                        }}
                    />
                    <Column
                        header={"Parámetros"}
                        style={{ width: "40%" }}
                        body={(row) => {
                            const metrics = getRowMetrics(row);

                            if (!metrics.length) {
                                return <span>-</span>;
                            }

                            const previewMetrics = metrics.slice(0, 2);
                            const hiddenCount = metrics.length - previewMetrics.length;

                            return (
                                <div style={{ width: "100%", minWidth: 0 }}>
                                    {previewMetrics.map(([metricKey, metricValue]) => (
                                        <div
                                            key={metricKey}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: "8px",
                                                marginBottom: "6px",
                                                marginRight: "10px",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: "var(--text-color)",
                                                    lineHeight: 1.2,
                                                    minWidth: 0,
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                {formatMetricLabel(metricKey)}
                                            </span>
                                            {renderMetricPill(metricValue)}
                                        </div>
                                    ))}
                                    {hiddenCount > 0 && (
                                        <span style={{ fontSize: "12px", color: "var(--text-color-secondary)" }}>
                                            +{hiddenCount} parámetro{hiddenCount > 1 ? "s" : ""}. Expande la fila para ver todo.
                                        </span>
                                    )}
                                </div>
                            );
                        }}
                    />
                    <Column
                        header={<></>}
                        style={{ width: "6%" }}
                        body={(row) => {
                            return (
                                <div className="d-flex justify-content-end me-3">
                                    <Button
                                        type="button"
                                        icon={<PiChatTeardropDotsFill size={"1.2rem"} />}
                                        className="action-body-btn action-body-btn-aqua"
                                        tooltip="Ver conversación"
                                        tooltipOptions={{ position: "top" }}
                                        onClick={() => {
                                            window.open(
                                                `/chats?dates=empty&type=all&chat=${row.sender_id}`,
                                                "_blank"
                                            );
                                        }}
                                        aria-label="Ver conversación"
                                        disabled={!row.sender_id}
                                    />
                                </div>
                            );
                        }}
                    />

                </DataTable>
            </div>
        </>
    );
};

export default TableChatsEvaluation;
